import { messages, type ApiErrorBody, type ErrorCode } from '@nesc/shared';

const BASE_URL = '/api/v1';

/** アクセストークンはメモリに保持する（localStorage には置かない：docs/11-security.md 2.1） */
let accessToken: string | null = null;

/**
 * セッションが存在しうるかの印。
 * リフレッシュ用 Cookie は HttpOnly のため JavaScript から読めない。
 * 未ログインで毎回 401 を出さないよう、この印がある場合だけ再発行を試みる。
 * 秘密情報は含まない。
 */
const SESSION_HINT_KEY = 'nesc.session';
export const hasSessionHint = (): boolean => localStorage.getItem(SESSION_HINT_KEY) === '1';
const setSessionHint = (value: boolean) => {
  if (value) localStorage.setItem(SESSION_HINT_KEY, '1');
  else localStorage.removeItem(SESSION_HINT_KEY);
};
let refreshPromise: Promise<boolean> | null = null;
let onUnauthenticated: (() => void) | null = null;

export const setAccessToken = (token: string | null) => {
  accessToken = token;
  setSessionHint(!!token);
};
export const getAccessToken = () => accessToken;
export const setUnauthenticatedHandler = (handler: () => void) => {
  onUnauthenticated = handler;
};

export class ApiError extends Error {
  readonly code: ErrorCode | string;
  readonly status: number;
  readonly details?: { field?: string; reason: string; hint?: string }[];
  readonly traceId?: string;
  readonly retryAfter?: number;

  constructor(status: number, body: ApiErrorBody['error']) {
    super(body.message);
    this.name = 'ApiError';
    this.status = status;
    this.code = body.code;
    this.details = body.details;
    this.traceId = body.traceId;
    this.retryAfter = body.retryAfter;
  }

  /** 項目単位のエラーを react-hook-form へ渡す形にする */
  get fieldErrors(): Record<string, string> {
    const out: Record<string, string> = {};
    for (const detail of this.details ?? []) {
      if (detail.field) out[detail.field] = detail.reason;
    }
    return out;
  }
}

export type RequestOptions = {
  method?: 'GET' | 'POST' | 'PATCH' | 'PUT' | 'DELETE';
  body?: unknown;
  query?: Record<string, unknown>;
  signal?: AbortSignal;
  /** 送信系 POST の重複実行を防ぐ */
  idempotencyKey?: string;
  formData?: FormData;
};

const buildUrl = (path: string, query?: Record<string, unknown>): string => {
  const url = new URL(`${BASE_URL}${path}`, window.location.origin);
  for (const [key, value] of Object.entries(query ?? {})) {
    if (value == null || value === '' || (Array.isArray(value) && value.length === 0)) continue;
    url.searchParams.set(key, Array.isArray(value) ? value.join(',') : String(value));
  }
  return `${url.pathname}${url.search}`;
};

/** アクセストークンが切れたら 1 度だけ再発行を試みる */
const refreshAccessToken = async (): Promise<boolean> => {
  if (refreshPromise) return refreshPromise;
  // 一度もログインしていない状態では再発行を試みない
  if (!hasSessionHint()) return false;

  refreshPromise = (async () => {
    try {
      const response = await fetch(`${BASE_URL}/auth/refresh`, {
        method: 'POST',
        credentials: 'include',
      });
      if (!response.ok) {
        setSessionHint(false);
        return false;
      }
      const json = (await response.json()) as { data: { accessToken: string } };
      accessToken = json.data.accessToken;
      setSessionHint(true);
      return true;
    } catch {
      return false;
    } finally {
      refreshPromise = null;
    }
  })();

  return refreshPromise;
};

const parseError = async (response: Response): Promise<ApiError> => {
  try {
    const body = (await response.json()) as ApiErrorBody;
    if (body?.error) return new ApiError(response.status, body.error);
  } catch {
    // JSON でない応答
  }
  return new ApiError(response.status, {
    code: 'INTERNAL_ERROR',
    message: messages.errors.internal(),
  });
};

export const apiRequest = async <T>(path: string, options: RequestOptions = {}): Promise<T> => {
  const send = async (): Promise<Response> => {
    const headers: Record<string, string> = {};
    if (accessToken) headers.authorization = `Bearer ${accessToken}`;
    if (options.idempotencyKey) headers['Idempotency-Key'] = options.idempotencyKey;
    if (options.body !== undefined && !options.formData) {
      headers['content-type'] = 'application/json; charset=utf-8';
    }

    return fetch(buildUrl(path, options.query), {
      method: options.method ?? 'GET',
      headers,
      credentials: 'include',
      signal: options.signal,
      body: options.formData ?? (options.body !== undefined ? JSON.stringify(options.body) : undefined),
    });
  };

  let response: Response;
  try {
    response = await send();
  } catch (err) {
    if (options.signal?.aborted) throw err;
    throw new ApiError(0, { code: 'UPSTREAM_UNAVAILABLE', message: messages.errors.networkOffline });
  }

  // 期限切れなら再発行してから 1 度だけ再試行する
  if (response.status === 401 && accessToken) {
    const refreshed = await refreshAccessToken();
    if (refreshed) {
      response = await send();
    } else {
      setAccessToken(null);
      onUnauthenticated?.();
    }
  }

  if (response.status === 204) return undefined as T;
  if (!response.ok) throw await parseError(response);

  const contentType = response.headers.get('content-type') ?? '';
  if (!contentType.includes('json')) return (await response.text()) as unknown as T;
  return (await response.json()) as T;
};

export type ListResponse<T> = {
  data: T[];
  meta: {
    count: number;
    hasMore: boolean;
    nextCursor: string | null;
    totalCount?: number;
    [key: string]: unknown;
  };
};
export type SingleResponse<T> = { data: T };

export const api = {
  get: <T>(path: string, query?: Record<string, unknown>, signal?: AbortSignal) =>
    apiRequest<T>(path, { method: 'GET', query, signal }),
  post: <T>(path: string, body?: unknown, options?: Omit<RequestOptions, 'method' | 'body'>) =>
    apiRequest<T>(path, { ...options, method: 'POST', body }),
  patch: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PATCH', body }),
  put: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'PUT', body }),
  del: <T>(path: string, body?: unknown) => apiRequest<T>(path, { method: 'DELETE', body }),
  upload: <T>(path: string, formData: FormData) =>
    apiRequest<T>(path, { method: 'POST', formData }),
  refresh: refreshAccessToken,
};

/** 認証つきでファイルを取得し、ブラウザに保存させる（添付ファイル等） */
export const downloadFile = async (path: string, fallbackName: string): Promise<void> => {
  const send = () =>
    fetch(buildUrl(path), {
      headers: accessToken ? { authorization: `Bearer ${accessToken}` } : {},
      credentials: 'include',
    });
  let response = await send();
  if (response.status === 401 && (await refreshAccessToken())) response = await send();
  if (!response.ok) throw await parseError(response);

  const disposition = response.headers.get('content-disposition') ?? '';
  const encoded = disposition.match(/filename\*=UTF-8''([^;]+)/i)?.[1];
  const name = encoded ? decodeURIComponent(encoded) : fallbackName;
  const url = URL.createObjectURL(await response.blob());
  const link = document.createElement('a');
  link.href = url;
  link.download = name;
  document.body.appendChild(link);
  link.click();
  link.remove();
  setTimeout(() => URL.revokeObjectURL(url), 10_000);
};

/** 例外から画面に出す文言を決める（docs/02-functional-spec.md 0.4） */
export const errorMessage = (error: unknown): string => {
  if (error instanceof ApiError) {
    if (error.status >= 500 || error.code === 'INTERNAL_ERROR') {
      return messages.errors.internal(error.traceId);
    }
    return error.message;
  }
  if (error instanceof Error) return error.message;
  return messages.errors.internal();
};
